import { z } from 'zod'

/**
 * SPEC.md §4.6 — the closed set of error codes.
 *
 * Every API failure is returned as `{ error: { code, message, details? } }` with
 * the HTTP status listed here, and every code has an `errors.<CODE>` entry in
 * both panel dictionaries (enforced by a parity test).
 */
export const ERROR_STATUS = {
  VALIDATION_ERROR: 400,
  AUTH_REQUIRED: 401,
  AUTH_INVALID_CREDENTIALS: 401,
  AUTH_TOKEN_EXPIRED: 401,
  AUTH_TOKEN_INVALID: 401,
  AUTH_REFRESH_REUSED: 401,
  AUTH_LOCKED: 423,
  AUTH_REGISTRATION_CLOSED: 403,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  RATE_LIMITED: 429,
  LICENSE_INVALID: 401,
  LICENSE_REVOKED: 403,
  LICENSE_SUSPENDED: 403,
  LICENSE_EXPIRED: 403,
  LICENSE_IP_MISMATCH: 403,
  AGENT_VERSION_UNSUPPORTED: 426,
  AGENT_SIGNATURE_INVALID: 401,
  HEARTBEAT_SEQ_REPLAY: 409,
  BATCH_TOO_LARGE: 413,
  CONFIG_VERSION_CONFLICT: 409,
  CONFIG_INVALID: 400,
  UPLOAD_TOKEN_INVALID: 404,
  UPLOAD_TOO_LARGE: 413,
  UPLOAD_BAD_MIME: 415,
  COMMAND_EXPIRED: 410,
  SERVER_OFFLINE: 409,
  GLOBAL_BAN_NOT_ELIGIBLE: 422,
  WEBHOOK_INVALID_URL: 400,
  INVITE_INVALID: 400,
  INTERNAL: 500,
} as const satisfies Record<string, number>

export const ERROR_CODES = Object.keys(ERROR_STATUS) as [ErrorCode, ...ErrorCode[]]
export type ErrorCode = keyof typeof ERROR_STATUS

export const ErrorCodeSchema = z.enum(Object.keys(ERROR_STATUS) as [ErrorCode, ...ErrorCode[]])

export function errorStatus(code: ErrorCode): number {
  return ERROR_STATUS[code]
}

export const ApiErrorSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

/**
 * The single error type thrown inside the API. The Fastify error handler turns
 * it into the envelope above with the status from `ERROR_STATUS`.
 */
export class SentinelError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly details: Record<string, unknown> | undefined
  /** Extra headers the handler should set, e.g. `Retry-After`. */
  readonly headers: Record<string, string> | undefined

  constructor(
    code: ErrorCode,
    message?: string,
    options: { details?: Record<string, unknown>; headers?: Record<string, string>; cause?: unknown } = {},
  ) {
    super(message ?? code, options.cause === undefined ? undefined : { cause: options.cause })
    this.name = 'SentinelError'
    this.code = code
    this.status = ERROR_STATUS[code]
    this.details = options.details
    this.headers = options.headers
  }

  toJSON(): ApiError {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details ? { details: this.details } : {}),
      },
    }
  }

  static isSentinelError(value: unknown): value is SentinelError {
    return value instanceof SentinelError
  }
}

/** Shorthand constructors for the codes used most often. */
export const errors = {
  validation: (message: string, details?: Record<string, unknown>) =>
    new SentinelError('VALIDATION_ERROR', message, { details: details ?? {} }),
  authRequired: (message = 'Authentication required') => new SentinelError('AUTH_REQUIRED', message),
  forbidden: (message = 'You do not have permission to do this') => new SentinelError('FORBIDDEN', message),
  notFound: (what = 'Resource') => new SentinelError('NOT_FOUND', `${what} not found`),
  conflict: (message: string, details?: Record<string, unknown>) =>
    new SentinelError('CONFLICT', message, { details: details ?? {} }),
  rateLimited: (retryAfterMs: number) =>
    new SentinelError('RATE_LIMITED', 'Too many requests', {
      details: { retryAfterMs },
      headers: { 'retry-after': String(Math.ceil(retryAfterMs / 1000)) },
    }),
  internal: (cause?: unknown) => new SentinelError('INTERNAL', 'Internal server error', { cause }),
} as const
